import React from 'react';
import styled from 'styled-components';
import Button from 'components/atoms/Button';
import Text from 'components/atoms/Text';
import ToggleButton from 'components/atoms/ToggleButton';
import Icon from 'components/atoms/Icon';
import { DateRangePicker } from 'components/molecules/DateRangePicker';
import { COLOR_SYSTEM } from 'components/design/design-system';
import { getLastWeek, getLast3Months, getLast6Months, getLastYear } from 'utils/dateUtils';
import { StatisticsUnit } from 'services/api/adminStatistics';

interface FilterProps {
  startDate: string;
  endDate: string;
  unit: StatisticsUnit;
  simplifyPremiumRole: boolean;
  excludeUnspecified: boolean;
  groupByStatus: boolean;
  groupByPromptType: boolean;
  groupByUserId: boolean;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onUnitChange: (unit: StatisticsUnit) => void;
  onSimplifyPremiumRoleChange: (checked: boolean) => void;
  onExcludeUnspecifiedChange: (checked: boolean) => void;
  onGroupByStatusChange: (checked: boolean) => void;
  onGroupByPromptTypeChange: (checked: boolean) => void;
  onGroupByUserIdChange: (checked: boolean) => void;
  onSearch: () => void;
  onCSVDownload: () => void;
  loading?: boolean;
}

const StatisticsFilter: React.FC<FilterProps> = ({
  startDate,
  endDate,
  unit,
  simplifyPremiumRole,
  excludeUnspecified,
  groupByStatus,
  groupByPromptType,
  groupByUserId,
  onStartDateChange,
  onEndDateChange,
  onUnitChange,
  onSimplifyPremiumRoleChange,
  onExcludeUnspecifiedChange,
  onGroupByStatusChange,
  onGroupByPromptTypeChange,
  onGroupByUserIdChange,
  onSearch,
  onCSVDownload,
  loading = false,
}) => {
  const handleQuickSelect = (type: '1week' | '3months' | '6months' | '1year') => {
    let dates;
    if (type === '1week') {
      dates = getLastWeek();
    } else if (type === '3months') {
      dates = getLast3Months();
    } else if (type === '6months') {
      dates = getLast6Months();
    } else {
      dates = getLastYear();
    }
    onStartDateChange(dates.startDate);
    onEndDateChange(dates.endDate);
  };

  return (
    <FilterContainer>
      <MainContent>
        <FiltersColumn>
          <FilterRow>
            <Label>
              <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[700]}>
                조회 기간
              </Text>
            </Label>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onChangeDate={(kind, date) => {
                const dateStr = date.toISOString().split('T')[0];
                if (kind === 'start') {
                  onStartDateChange(dateStr);
                } else {
                  onEndDateChange(dateStr);
                }
              }}
              disabled={loading}
            />
            <QuickButtonGroup>
              <Button
                size="Small"
                type="Text"
                variant="Outlined"
                color="Primary"
                onClick={() => handleQuickSelect('1week')}
                disabled={loading}
              >
                1주일
              </Button>
              <Button
                size="Small"
                type="Text"
                variant="Outlined"
                color="Primary"
                onClick={() => handleQuickSelect('3months')}
                disabled={loading}
              >
                3개월
              </Button>
              <Button
                size="Small"
                type="Text"
                variant="Outlined"
                color="Primary"
                onClick={() => handleQuickSelect('6months')}
                disabled={loading}
              >
                6개월
              </Button>
              <Button
                size="Small"
                type="Text"
                variant="Outlined"
                color="Primary"
                onClick={() => handleQuickSelect('1year')}
                disabled={loading}
              >
                1년
              </Button>
            </QuickButtonGroup>
          </FilterRow>

          <FilterRow>
            <Label>
              <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[700]}>
                시간 단위
              </Text>
            </Label>
            <ToggleButtonGroup>
              <ToggleButton
                color="Blue"
                size="Small"
                type="Text"
                variant="Outlined"
                active={unit === 'day'}
                onClick={() => onUnitChange('day')}
                disabled={loading}
              >
                일
              </ToggleButton>
              <ToggleButton
                color="Blue"
                size="Small"
                type="Text"
                variant="Outlined"
                active={unit === 'week'}
                onClick={() => onUnitChange('week')}
                disabled={loading}
              >
                주
              </ToggleButton>
              <ToggleButton
                color="Blue"
                size="Small"
                type="Text"
                variant="Outlined"
                active={unit === 'month'}
                onClick={() => onUnitChange('month')}
                disabled={loading}
              >
                월
              </ToggleButton>
              <ToggleButton
                color="Blue"
                size="Small"
                type="Text"
                variant="Outlined"
                active={unit === 'all'}
                onClick={() => onUnitChange('all')}
                disabled={loading}
              >
                전체
              </ToggleButton>
            </ToggleButtonGroup>
          </FilterRow>

          <FilterRow>
            <Label>
              <Text textType="Body2" color={COLOR_SYSTEM.get('Gray')?.[700]}>
                그룹핑 옵션
              </Text>
            </Label>
            <ToggleButtonGroup>
              <ToggleButton
                color="Blue"
                size="Small"
                type="Text"
                variant="Outlined"
                active={simplifyPremiumRole}
                onClick={() => onSimplifyPremiumRoleChange(!simplifyPremiumRole)}
                disabled={loading}
              >
                Role 단순화
              </ToggleButton>
              <ToggleButton
                color="Blue"
                size="Small"
                type="Text"
                variant="Outlined"
                active={groupByStatus}
                onClick={() => onGroupByStatusChange(!groupByStatus)}
                disabled={loading}
              >
                Status별
              </ToggleButton>
              <ToggleButton
                color="Blue"
                size="Small"
                type="Text"
                variant="Outlined"
                active={groupByPromptType}
                onClick={() => onGroupByPromptTypeChange(!groupByPromptType)}
                disabled={loading}
              >
                PromptType별
              </ToggleButton>
              <ToggleButton
                color="Blue"
                size="Small"
                type="Text"
                variant="Outlined"
                active={groupByUserId}
                onClick={() => onGroupByUserIdChange(!groupByUserId)}
                disabled={loading}
              >
                사용자별
              </ToggleButton>
            </ToggleButtonGroup>

            <Separator />

            <ExcludeUnspecifiedButton
              onClick={() => onExcludeUnspecifiedChange(!excludeUnspecified)}
              disabled={loading}
              $active={excludeUnspecified}
            >
              <Icon name={excludeUnspecified ? "Eye Closed" : "Eye"} size={16} />
              <Text textType="Body2" color={excludeUnspecified ? COLOR_SYSTEM.get('Blue')?.[600] : COLOR_SYSTEM.get('Gray')?.[700]}>
                미지정 제외
              </Text>
            </ExcludeUnspecifiedButton>
          </FilterRow>
        </FiltersColumn>

        <ActionButtonGroup>
          <Button
            size="Large"
            type="Text"
            variant="Contained"
            color="Primary"
            onClick={onSearch}
            disabled={loading}
          >
            조회
          </Button>
          <Button
            size="Large"
            type="IconWithText"
            variant="Outlined"
            color="Primary"
            icon="Download"
            left
            onClick={onCSVDownload}
            disabled={loading}
          >
            CSV 다운로드
          </Button>
        </ActionButtonGroup>
      </MainContent>
    </FilterContainer>
  );
};

const FilterContainer = styled.div`
  display: flex;
  padding: 16px 24px;
  background-color: ${COLOR_SYSTEM.get('Gray')?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get('Gray')?.[200]};
  border-radius: 8px;
`;

const MainContent = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
`;

const FiltersColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Label = styled.div`
  min-width: 80px;
  white-space: nowrap;
`;

const QuickButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ToggleButtonGroup = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const ActionButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-self: center;
`;

const Separator = styled.div`
  width: 1px;
  height: 24px;
  background-color: ${COLOR_SYSTEM.get('Gray')?.[300]};
  margin: 0 12px;
`;

const ExcludeUnspecifiedButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${(props) => props.$active ? COLOR_SYSTEM.get('Blue')?.[600] : COLOR_SYSTEM.get('Gray')?.[300]};
  border-radius: 4px;
  background-color: ${(props) => props.$active ? COLOR_SYSTEM.get('Blue')?.[50] : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: ${(props) => props.$active ? COLOR_SYSTEM.get('Blue')?.[100] : COLOR_SYSTEM.get('Gray')?.[50]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default StatisticsFilter;
